import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchBusinessSettings } from "../../store/businessSlice";
import { baseurImage } from "../../Baseurl";
import axiosInstance from "../../api/axiosInstance";

const NUMPAD = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["clear", "0", "back"],
];

export default function Kiosk() {
  const [cpr, setCpr] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const businessSettings = useSelector((state) => state.business.businessSettings);

  React.useEffect(() => {
    if (!businessSettings) {
      const stored = localStorage.getItem("businessSetting");
      if (stored) {
        try {
          const bs = JSON.parse(stored);
          if (bs?.id) dispatch(fetchBusinessSettings(bs.id));
        } catch {}
      }
    }
  }, []);

  const getLogoSrc = () => {
    const raw = businessSettings?.image || businessSettings?.logo;
    if (!raw) return null;
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${baseurImage}${raw}`;
  };

  const handleKey = (key) => {
    setError("");
    if (key === "back") {
      setCpr((v) => v.slice(0, -1));
    } else if (key === "clear") {
      setCpr("");
    } else {
      if (cpr.length < 9) setCpr((v) => v + key);
    }
  };

  const handleProceed = async () => {
    if (cpr.length !== 9 || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get(`getPatientByCivilId/${cpr}`);
      if (res.data?.success && res.data?.data) {
        navigate("/Admin/Kiosk/checkin", { state: { patient: res.data.data } });
      } else {
        setError("ID doesn't exist, please visit reception.");
        setCpr("");
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setError("ID doesn't exist, please visit reception.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      setCpr("");
    } finally {
      setLoading(false);
    }
  };

  const logoSrc = getLogoSrc();
  const ready = cpr.length === 9 && !loading;

  return (
    <div style={styles.root}>
      {/* Logo */}
      <div style={styles.logoWrap}>
        {logoSrc ? (
          <img src={logoSrc} alt="Logo" style={styles.logo} />
        ) : (
          <div style={styles.logoPlaceholder}>
            <i className="ti ti-heart-plus" style={{ fontSize: 48, color: "#4f8ef7" }} />
          </div>
        )}
      </div>

      <p style={styles.welcome}>Welcome</p>
      <p style={styles.subtitle}>Please enter your CPR ID to check in</p>

      {/* CPR display */}
      <div style={styles.cprDisplay}>
        {Array.from({ length: 9 }).map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.cprSlot,
              ...(i < cpr.length ? styles.cprSlotFilled : {}),
            }}
          >
            {i < cpr.length ? (
              <span style={styles.cprDot}>●</span>
            ) : (
              <span style={styles.cprEmpty}>-</span>
            )}
          </div>
        ))}
      </div>

      {/* Error message */}
      {error && (
        <div style={styles.errorBox}>
          <i className="ti ti-alert-circle me-2" style={{ fontSize: 20 }} />
          {error}
        </div>
      )}

      {/* Numpad */}
      <div style={styles.numpad}>
        {NUMPAD.map((row, ri) => (
          <div key={ri} style={styles.row}>
            {row.map((key) => {
              const isAction = key === "back" || key === "clear";
              return (
                <button
                  key={key}
                  style={{
                    ...styles.key,
                    ...(isAction ? styles.keyAction : {}),
                  }}
                  onClick={() => handleKey(key)}
                  onTouchStart={(e) => (e.currentTarget.style.transform = "scale(0.94)")}
                  onTouchEnd={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.94)")}
                  onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
                >
                  {key === "back" ? (
                    <i className="ti ti-backspace" style={{ fontSize: 28 }} />
                  ) : key === "clear" ? (
                    <i className="ti ti-x" style={{ fontSize: 24 }} />
                  ) : (
                    key
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Proceed button */}
      <button
        style={{ ...styles.proceedBtn, ...(ready ? styles.proceedBtnActive : {}) }}
        onClick={handleProceed}
        disabled={!ready}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm me-2" />
        ) : (
          <>
            Proceed
            <i className="ti ti-arrow-right ms-2" style={{ fontSize: 22 }} />
          </>
        )}
      </button>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(160deg, #f0f4ff 0%, #ffffff 60%, #eaf6f6 100%)",
    padding: "32px 24px",
    boxSizing: "border-box",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  logoWrap: { marginBottom: 20 },
  logo: { height: 90, maxWidth: 260, objectFit: "contain" },
  logoPlaceholder: {
    width: 90, height: 90, borderRadius: 20, background: "#eef3ff",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  welcome: { fontSize: 36, fontWeight: 700, color: "#1a2340", margin: "0 0 6px", letterSpacing: "-0.5px" },
  subtitle: { fontSize: 17, color: "#6b7a99", margin: "0 0 32px" },
  cprDisplay: { display: "flex", gap: 10, marginBottom: 20 },
  cprSlot: {
    width: 46, height: 56, borderRadius: 12, border: "2px solid #d0d8e8",
    background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    transition: "border-color 0.15s",
  },
  cprSlotFilled: { borderColor: "#4f8ef7", background: "#f0f5ff" },
  cprDot: { fontSize: 20, color: "#1a2340", lineHeight: 1 },
  cprEmpty: { fontSize: 22, color: "#c8d0e0", lineHeight: 1 },
  errorBox: {
    display: "flex", alignItems: "center",
    background: "#fff2f2", border: "1px solid #ffb3b3", borderRadius: 12,
    color: "#c0392b", padding: "12px 20px", fontSize: 15, fontWeight: 500,
    marginBottom: 20, maxWidth: 370, textAlign: "center",
  },
  numpad: { display: "flex", flexDirection: "column", gap: 14, marginBottom: 32 },
  row: { display: "flex", gap: 14 },
  key: {
    width: 110, height: 80, borderRadius: 18, border: "none",
    background: "#fff", boxShadow: "0 2px 12px rgba(60,80,140,0.10)",
    fontSize: 32, fontWeight: 600, color: "#1a2340", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "transform 0.1s, box-shadow 0.1s", userSelect: "none",
    WebkitTapHighlightColor: "transparent",
  },
  keyAction: { background: "#f0f4ff", color: "#4f8ef7", fontSize: 22 },
  proceedBtn: {
    width: 348, height: 64, borderRadius: 18, border: "none",
    background: "#c8d8f8", color: "#fff", fontSize: 20, fontWeight: 600,
    cursor: "not-allowed", display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.2s, transform 0.1s", userSelect: "none",
    WebkitTapHighlightColor: "transparent",
  },
  proceedBtnActive: {
    background: "linear-gradient(135deg, #4f8ef7 0%, #2563eb 100%)",
    boxShadow: "0 4px 20px rgba(79,142,247,0.35)", cursor: "pointer",
  },
};
